import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../constants";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Brush,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { FaEdit } from 'react-icons/fa';


function Graphique(props) {
  // Props pour cacher l'unité de l'axe Y et la courbe de moyenne
  const hideYAxisUnit = props.hideYAxisUnit || false;
  const hideMeanValue = props.hideMeanValue || false;
  const [isEditing, setIsEditing] = useState(false);
  const [graphName, setGraphName] = useState(props.name);
  const [means, setMeans] = useState({});

  const data = props.valdata;
  const type = props.type;

  const motor = localStorage.getItem('motor') ? JSON.parse(localStorage.getItem('motor')) : props.motor;

  // Fonction pour déterminer la couleur de fond en fonction du typeDefaut
  const getBackgroundColor = (graphName) => {
    if (!motor) return props.backgroundColor || "transparent";
    // Pas de couleur pour les graphiques d'amplitude
    if (graphName.includes("Amplitude des vibrations")) {
      return "transparent";
    }

    // Helper pour normaliser
    const normalize = (val) => typeof val === 'string' ? val.toLowerCase() : "null";

    let typeDefaut = "null";
    let typeDefautVitesse = "null";

    if (graphName.includes("axe X")) {
      typeDefaut = normalize(motor.typeDefautX1);
      typeDefautVitesse = normalize(motor.typeDefautVitesseX1);
    } else if (graphName.includes("axe Y")) {
      typeDefaut = normalize(motor.typeDefautY1);
      typeDefautVitesse = normalize(motor.typeDefautVitesseY1);
    } else if (graphName.includes("axe Z")) {
      typeDefaut = normalize(motor.typeDefautZ1);
      typeDefautVitesse = normalize(motor.typeDefautVitesseZ1);
    }

    // Priorité : rouge > jaune > transparent
    if (typeDefautVitesse === "rouge" || typeDefaut === "rouge") {
      return "#ffcccc"; // Rouge clair
    } else if (typeDefautVitesse === "jaune" || typeDefaut === "jaune") {
      return "#fff9cc"; // Jaune clair
    } else {
      return "transparent";
    }
  };

  const backgroundColor = getBackgroundColor(props.name);

  const dataWithoutUndefined = props.valdata.map((item) => ({
    ...item,
    ordonne: item.ordonne || 0,
  }));

  useEffect(() => {
    if (!motor) {
      setGraphName(props.name);
      return;
    }
    
    const axeX = motor.axeX || 'X';
    const axeY = motor.axeY || 'Y';
    const axeZ = motor.axeZ || 'Z';
    
    if (props.name === "Vitesse RMS axe X") {
      setGraphName(`Vitesse RMS axe ${axeX}`);
    } else if (props.name === "Vitesse RMS axe Y") {
      setGraphName(`Vitesse RMS axe ${axeY}`);
    } else if (props.name === "Vitesse RMS axe Z") {
      setGraphName(`Vitesse RMS axe ${axeZ}`);
    } else if (props.name === "Amplitude des vibrations axe X") {
      setGraphName(`Amplitude des vibrations axe ${axeX}`);
    } else if (props.name === "Amplitude des vibrations axe Y") {
      setGraphName(`Amplitude des vibrations axe ${axeY}`);
    } else if (props.name === "Amplitude des vibrations axe Z") {
      setGraphName(`Amplitude des vibrations axe ${axeZ}`);
    } else if (props.name === "Etat vibratoire axe X") {
      setGraphName(`Etat vibratoire axe ${axeX}`);
    } else if (props.name === "Etat vibratoire axe Y") {
      setGraphName(`Etat vibratoire axe ${axeY}`);
    } else if (props.name === "Etat vibratoire axe Z") {
      setGraphName(`Etat vibratoire axe ${axeZ}`);
    } else {
      setGraphName(props.name);
    }
  }, [props.name, motor]);

  useEffect(() => {
    // Récupère les moyennes RMS dynamiquement
    async function fetchMeans() {
      if (!motor || !motor._id) return;
      try {
        const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
        const token = user?.token || '';
        const res = await axios.get(`${API_URL}/trameshisto/histo?id=${motor._id}`,
          {
            headers: {
              mode: "cors",
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
        const data = res.data?.data?.[0] || {};
        setMeans({
          X1: data.vitesseX1RMS,
          X2: data.vitesseX2RMS,
          Y1: data.vitesseY1RMS,
          Y2: data.vitesseY2RMS,
          Z1: data.vitesseZ1RMS,
          Z2: data.vitesseZ2RMS,
        });
      } catch (e) {
        setMeans({});
      }
    }
    fetchMeans();
  }, [motor?._id]);

  const handleInputChange = (e) => {
    setGraphName(e.target.value);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    
    // Déterminer quel axe a été modifié et sa nouvelle valeur
    const axeMapping = {
      "Vitesse RMS axe X": "axeX",
      "Vitesse RMS axe Y": "axeY",
      "Vitesse RMS axe Z": "axeZ",
      "Amplitude des vibrations axe X": "axeX",
      "Amplitude des vibrations axe Y": "axeY",
      "Amplitude des vibrations axe Z": "axeZ"
    };

    const axeKey = axeMapping[props.name];
    if (!axeKey) return;

    // Extraire la lettre de l'axe depuis le nouveau nom (ex: "Vitesse RMS axe Z" -> "Z")
    const match = graphName.match(/axe ([XYZ])/);
    if (!match) return;

    const newAxisLetter = match[1];

    try {
      const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
      const token = user?.token || '';
      
      await axios.post(
        `${API_URL}/moteur/axes`,
        {
          id_moteur: motor._id,
          [axeKey]: newAxisLetter
        },
        {
          headers: {
            mode: "cors",
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      
      // Rafraîchir la page ou les données après la modification
      window.location.reload();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'axe:", error);
      // Restaurer le nom précédent en cas d'erreur
      setGraphName(props.name);
    }
  };

  // Liste des graphiques éditables
  const editableGraphs = [
    "Vitesse RMS axe X", "Vitesse RMS axe Y", "Vitesse RMS axe Z",
    "Amplitude des vibrations axe X", "Amplitude des vibrations axe Y", "Amplitude des vibrations axe Z"
  ];

  const isEditable = editableGraphs.includes(props.ordonne) || editableGraphs.includes(props.name);

  let graph;

  // Fonction de rendu personnalisée pour la légende
  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div className="flex justify-center items-center mt-4 gap-6">
        {payload.map((entry, index) => (
          <div key={`item-${index}`} className="flex items-center gap-2">
            <div 
              style={{
                width: '24px',
                height: entry.value === 'Moyenne' ? '3px' : '3px',
                backgroundColor: entry.color,
                borderStyle: entry.value === 'Moyenne' ? 'dashed' : 'solid',
                borderWidth: entry.value === 'Moyenne' ? '2px 0' : '0',
                borderColor: entry.value === 'Moyenne' ? entry.color : 'transparent',
              }}
            />
            <span className="text-sm font-medium text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const colorCurrent = (valeur) => (valeur > props.valdata[0].ordonne * 1.1 ? "red" : "#8884d8");

  const color = (valeur) => {
    switch (type) {
      case "type1":
        if (valeur < 42) {
          return "green";
        }
        if (valeur < 84) {
          return "yellow";
        }
        if (valeur < 135) {
          return "orange";
        }
        return "red";

      case "type2":
      case "type3":
        if (valeur < 69) {
          return "green";
        }
        if (valeur < 135) {
          return "yellow";
        }
        if (valeur < 213) {
          return "orange";
        }
        return "red";

      case "type4":
        if (valeur < 105) {
          return "green";
        }
        if (valeur < 213) {
          return "yellow";
        }
        if (valeur < 330) {
          return "orange";
        }
        return "red";

      default:
        return "indigo";
    }
  };

  if (props.ordonne === "temperature") {
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 30, bottom: 10, left: 30 }} data={props.valdata}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            name={props.abscisse}
            unit={props.uniteabscisse}
          />
          <YAxis
            type={props.typeY}
            dataKey={props.keyord}
            name={props.ordonne}
            unit={props.uniteordonne}
          />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Tooltip cursor={{ strokeDasharray: "3 3" }} />
          <Line
            name="donnee"
            dataKey={props.keyord}
            stroke="indigo"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  } else if (props.ordonne === "velocité") {
    // Association statique : la moyenne reste liée à l'axe d'origine
    let mean = 0;
    // Détection des graphes "Etat vibratoire axe X/Y/Z" (pour clients)
    const isEtatVibratoire = ["Etat vibratoire axe X", "Etat vibratoire axe Y", "Etat vibratoire axe Z"].includes(props.name);
    if (props.name === "Vitesse RMS axe X" || props.name === "Etat vibratoire axe X") mean = means.X1 ?? 0;
    else if (props.name === "Vitesse RMS axe Y" || props.name === "Etat vibratoire axe Y") mean = means.Y1 ?? 0;
    else if (props.name === "Vitesse RMS axe Z" || props.name === "Etat vibratoire axe Z") mean = means.Z1 ?? 0;
    const showMean = props.granularity === "day" && (!props.hideMeanValue || isEtatVibratoire);
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 62, bottom: 10, left: 50 }} data={props.valdata} barSize={10}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
          />
          {isEtatVibratoire ? (
            <YAxis hide={true} />
          ) : (
            <YAxis unit={props.hideYAxisUnit ? "" : props.uniteordonne} />
          )}
          <Tooltip
            content={isEtatVibratoire ? (({ label }) => label ? <div style={{ background: 'white', padding: 4, borderRadius: 4, fontWeight: 600 }}>{label}</div> : null) : undefined}
          />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          {/* Afficher la légende uniquement pour les graphiques "Etat vibratoire" avec moyenne */}
          {isEtatVibratoire && showMean && <Legend content={renderLegend} />}
          <Line
            type="linear"
            name={isEtatVibratoire ? "Vibrations" : props.ordonne}
            dataKey={props.keyord}
            stroke="#8884d8"
            strokeWidth={2}
            dot={false}
          />
          {showMean && (
            <Line
              type="linear"
              name="Moyenne"
              dataKey={() => mean}
              stroke="#ff9900"
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  }  else if (props.ordonne === "frequence") {
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 62, bottom: 10, left: 40 }} data={props.valdata} barSize={10}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
          />
          <YAxis unit={props.uniteordonne} />
          <Tooltip />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Line
            type="linear"
            name={props.ordonne}
            dataKey={props.keyord}
            stroke={(props.ordonne === "fréquence" && props.valdata && props.valdata[0]) ? colorCurrent(props.valdata[0].courant) : "#8884d8"}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  } else if (props.ordonne === "signal") {
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 62, bottom: 10, left: 40 }} data={props.valdata} barSize={10}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
            interval={Math.ceil(props.valdata.length / 5) - 1}
          />
          <YAxis 
            unit={props.uniteordonne} 
            reversed 
            tickFormatter={(val) => Number(val).toFixed(2)} 
          />
          <Tooltip 
            formatter={(val) => Number(val).toFixed(2) + " " + props.uniteordonne} 
          />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Line
            type="linear"
            name={props.ordonne}
            dataKey={props.keyord}
            stroke={(props.ordonne === "signal" && props.valdata && props.valdata[0]) ? colorCurrent(props.valdata[0].courant) : "#8884d8"}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  } else if (props.ordonne === "etat_vibratoire") {
    // Fonction helper pour normaliser les valeurs
    const normalizeDefaut = (val) => typeof val === 'string' ? val.toLowerCase() : "null";
    
    // Fonction pour déterminer le niveau de gravité (3=rouge, 2=jaune, 1=vert/null)
    const getSeverity = (defaut) => {
      if (defaut === "rouge") return 3;
      if (defaut === "jaune") return 2;
      return 1;
    };

    // Analyser tous les typeDefaut (dérive vibratoire)
    const typeDefauts = [
      motor?.typeDefautX1 || "null",
      motor?.typeDefautY1 || "null", 
      motor?.typeDefautZ1 || "null"
    ].map(normalizeDefaut);

    let typeDefautDerive = "null";
    if (typeDefauts.includes("rouge")) {
      typeDefautDerive = "rouge";
    } else if (typeDefauts.includes("jaune")) {
      typeDefautDerive = "jaune";
    }

    // Analyser tous les typeDefautVitesse (état vibratoire)
    const typeDefautsVitesse = [
      motor?.typeDefautVitesseX1 || "null",
      motor?.typeDefautVitesseX2 || "null",
      motor?.typeDefautVitesseY1 || "null",
      motor?.typeDefautVitesseY2 || "null",
      motor?.typeDefautVitesseZ1 || "null",
      motor?.typeDefautVitesseZ2 || "null"
    ].map(normalizeDefaut);

    let typeDefautEtat = "null";
    if (typeDefautsVitesse.includes("rouge")) {
      typeDefautEtat = "rouge";
    } else if (typeDefautsVitesse.includes("jaune")) {
      typeDefautEtat = "jaune";
    }

    // Déterminer l'erreur principale (la plus grave)
    const severityDerive = getSeverity(typeDefautDerive);
    const severityEtat = getSeverity(typeDefautEtat);
    
    let erreurPrincipale, erreurSecondaire;
    if (severityDerive >= severityEtat) {
      erreurPrincipale = { type: "derive", niveau: typeDefautDerive };
      erreurSecondaire = severityEtat > 1 ? { type: "etat", niveau: typeDefautEtat } : null;
    } else {
      erreurPrincipale = { type: "etat", niveau: typeDefautEtat };
      erreurSecondaire = severityDerive > 1 ? { type: "derive", niveau: typeDefautDerive } : null;
    }

    // Vérifier s'il y a une erreur de courant anormal
    const erreurs = motor?.etatVibratoire || [];
    const erreurCourantAnormal = erreurs.includes("Possibles défauts du moteur dû à la consommation de courant");

    // Générer les messages
    const getMessagePart = (type, niveau) => {
      const prefix = type === "derive" ? "DÉRIVE VIBRATOIRE" : "ÉTAT VIBRATOIRE";
      if (niveau === "rouge") return `${prefix} IMPORTANT${type === "derive" ? "E" : ""}`;
      if (niveau === "jaune") return `${prefix} À SURVEILLER`;
      return `${prefix} NORMAL${type === "derive" ? "E" : ""}`;
    };

    let messagePrincipal = getMessagePart(erreurPrincipale.type, erreurPrincipale.niveau);
    let messageSecondaire = erreurSecondaire ? getMessagePart(erreurSecondaire.type, erreurSecondaire.niveau) : null;

    // Si les deux sont normaux, afficher un message combiné
    if (erreurPrincipale.niveau !== "rouge" && erreurPrincipale.niveau !== "jaune" && 
        (!erreurSecondaire || (erreurSecondaire.niveau !== "rouge" && erreurSecondaire.niveau !== "jaune"))) {
      messagePrincipal = "ÉTAT ET DÉRIVE VIBRATOIRE NORMAUX";
      messageSecondaire = null;
    }

    // Déterminer la couleur et l'icône selon l'erreur principale
    let bgColor = "";
    let icon = null;
    let phoneElement = null;

    if (erreurPrincipale.niveau === "rouge") {
      const typeErreur = erreurPrincipale.type === "derive" ? "DÉRIVE" : "ÉTAT";
      messagePrincipal = `${typeErreur} VIBRATOIRE IMPORTANT${erreurPrincipale.type === "derive" ? "E" : ""} ! FAIRE CONTRÔLER LA MACHINE PAR STB`;
      bgColor = "#ff7d7d";
      phoneElement = (
        <a
          href="tel:+33561064244"
          aria-label="Appeler STB 05 61 06 42 44"
          className="mt-4 inline-block text-lg md:text-xl lg:text-2xl font-bold text-red-900 bg-white/10 px-4 py-2 rounded-lg break-words"
          style={{ textDecoration: 'none' }}
        >
          Appeler STB au 05 61 06 42 44
        </a>
      );
      icon = (
        <svg
          className="mb-6"
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc3545"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    } else if (erreurPrincipale.niveau === "jaune") {
      bgColor = "#f3f907";
      icon = (
        <svg
          className="mb-6"
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="#fff3cd"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2l8 4v6c0 5-3.5 9.74-8 11-4.5-1.26-8-6-8-11V6l8-4z"
            stroke="#ffc107"
            strokeWidth="2.5"
            fill="#fff3cd"
          />
          <line x1="12" y1="8" x2="12" y2="13" stroke="#ffc107" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1.5" fill="#ffc107" />
        </svg>
      );
    } else {
      bgColor = "#86c80f";
      icon = (
        <svg
          className="mb-6"
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#28a745"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }

    // Si erreur courant anormal et que ce n'était pas rouge, passer en orange
    if (erreurCourantAnormal && erreurPrincipale.niveau !== "rouge") {
      bgColor = "#ef9d3c";
    }

    graph = (
      <div
        className="w-full rounded-xl p-8 flex flex-col items-center justify-center text-center mt-6 mb-6"
        style={{
          backgroundColor: bgColor,
          minHeight: 180,
          color: "#333",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        {icon}
        <p className="text-2xl md:text-3xl font-extrabold leading-snug max-w-3xl px-6">
          {messagePrincipal}
        </p>

        {messageSecondaire && (
          <p className="text-lg md:text-xl font-semibold mt-3 text-gray-700 opacity-90">
            {messageSecondaire}
          </p>
        )}

        {phoneElement && (
          <div className="mt-4">
            {phoneElement}
          </div>
        )}

        {erreurCourantAnormal && (
          <p className="text-xl text-red-700 font-medium mt-4">
            ⚠ Courant anormal
          </p>
        )}
      </div>
    );
  } else if (props.ordonne === "courant") {
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 62, bottom: 10, left: 40 }} data={props.valdata} barSize={10}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
          />
          <YAxis unit={props.uniteordonne} />
          <Tooltip />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Line
            type="linear"
            name={props.ordonne}
            dataKey={props.keyord}
            stroke={(props.ordonne === "courant" && props.valdata && props.valdata[0]) ? colorCurrent(props.valdata[0].courant) : "#8884d8"}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  } else if (props.ordonne === "consommation") {
    graph = (
      <ResponsiveContainer height={250}>
        <LineChart margin={{ top: 20, right: 62, bottom: 10, left: 40 }} data={dataWithoutUndefined}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
          />
          <YAxis unit={props.uniteordonne} />
          <Tooltip />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Line
            type="linear"
            name={props.ordonne}
            dataKey={props.keyord}
            stroke={(props.ordonne === "consommation" && props.valdata && props.valdata[0]) ? colorCurrent(props.valdata[0].courant) : "#8884d8"}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  } else {
    graph = (
      <ResponsiveContainer height={250}>
        <BarChart margin={{ top: 20, right: 62, bottom: 10, left: 40 }} data={props.valdata} barSize={2}>
          <CartesianGrid strokeDasharray="10 1" />
          <XAxis
            type={props.typeX}
            dataKey={props.keyabs}
            unit={props.uniteabscisse}
            padding={{ right: 10 }}
          />
          <YAxis unit={props.uniteordonne} />
          <Tooltip
            labelFormatter={(label, payload) => {
              const timestamp = payload[0]?.payload?.timestamp || '';
              return `${timestamp} | | ${label}`;
            }}
            formatter={(value, name) => [`${name}: ${value}`]}
          />
          <Brush dataKey={props.keyabs} stroke="indigo" />
          <Bar name={props.ordonne} dataKey={props.keyord} fill="indigo">
            {props.valdata.map((entry, index) => (
              <Cell fill={color(entry.ampli)} key={index} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Déterminer si la couleur de fond est jaune ou rouge (donc bordure noire)
  const hasWarningBg = backgroundColor === "#ffcccc" || backgroundColor === "#fff9cc";

  return (
    <div 
      className="flex flex-col justify-center items-center w-full lg:w-1/2 m-1 lg:m-auto mb-10 p-4 rounded-lg"
      style={{ backgroundColor: backgroundColor, border: hasWarningBg ? '3px solid #111' : undefined }}
    >
      <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl ml-5 font-bold text-center">
        {isEditing && graphName ? (
          <input
            type="text"
            value={graphName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                handleBlur();
              }
            }}
            autoFocus
            className="border-2 border-gray-300 rounded-md p-2 focus:outline-none focus:border-blue-500"
          />
        ) : (
          <div className="flex items-center">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl ml-5 font-bold cursor-pointer">
              {graphName}
            </h2>
            {editableGraphs.includes(graphName) && (
              <FaEdit
                className="ml-4 cursor-pointer hover:text-blue-600"
                onClick={() => setIsEditing(true)}
              />
            )}
          </div>
        )}
      </div>
      {graph} 
    </div>
  );
}

export default Graphique;